/**
 * Phase 2.5: Structured Note Parser
 * 
 * Parses free-form dictation transcript into structured operative note sections.
 * CRITICAL: This ONLY organizes surgeon-spoken words into sections.
 * It NEVER invents, rewrites, or adds clinical content.
 */

import type { StructuredOpNote } from './types';
import { EMPTY_STRUCTURED_NOTE } from './types';

/** Section detection patterns — order matters (first match wins) */
const SECTION_PATTERNS: Array<{
  key: keyof Omit<StructuredOpNote, 'fullText'>;
  patterns: RegExp[];
}> = [
  {
    key: 'indications',
    patterns: [
      /\b(?:indication|indications|reason for surgery|history|preoperative diagnosis)\b/i,
    ],
  },
  {
    key: 'findings',
    patterns: [
      /\b(?:findings|operative findings|intraoperative findings|upon (?:inspection|exposure|examination))\b/i,
    ],
  },
  {
    key: 'implants',
    patterns: [
      /\b(?:implants?|implants? used|components?|prosthes[ie]s|hardware)\b/i,
    ],
  },
  {
    key: 'closure',
    patterns: [
      /\b(?:closure|closing|wound closure|skin closure|dressing|dressings applied)\b/i,
    ],
  },
  {
    key: 'complications',
    patterns: [
      /\b(?:complications?)\b/i,
    ],
  },
  {
    key: 'anesthesia',
    patterns: [
      /\b(?:anesthesia|anesthetic)\b/i,
    ],
  },
  {
    key: 'specimens',
    patterns: [
      /\b(?:specimens?|pathology)\b/i,
    ],
  },
  {
    key: 'estimatedBloodLoss',
    patterns: [
      /\b(?:estimated blood loss|ebl|blood loss)\b/i,
    ],
  },
  {
    key: 'procedures',
    patterns: [
      /\b(?:procedure|procedures? performed|technique|operative technique|procedure details|description of procedure|surgical technique)\b/i,
    ],
  },
];

/**
 * Parse a free-form transcript into structured sections.
 * All content is verbatim from the transcript — no generation.
 */
export function parseTranscriptToStructuredNote(transcript: string): StructuredOpNote {
  if (!transcript.trim()) return { ...EMPTY_STRUCTURED_NOTE };

  const note: StructuredOpNote = { ...EMPTY_STRUCTURED_NOTE, fullText: transcript };
  const lines = transcript.split('\n');
  
  let currentSection: keyof Omit<StructuredOpNote, 'fullText'> | null = null;
  const sectionContent: Record<string, string[]> = {};

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Check if this line starts a new section
    let matchedSection: keyof Omit<StructuredOpNote, 'fullText'> | null = null;
    for (const { key, patterns } of SECTION_PATTERNS) {
      if (patterns.some(p => p.test(trimmed))) {
        matchedSection = key;
        break;
      }
    }

    if (matchedSection) {
      currentSection = matchedSection;
      if (!sectionContent[currentSection]) {
        sectionContent[currentSection] = [];
      }
      // Include the header line content after the colon if present
      const colonIdx = trimmed.indexOf(':');
      if (colonIdx !== -1) {
        const after = trimmed.substring(colonIdx + 1).trim();
        if (after) sectionContent[currentSection].push(after);
      }
    } else if (currentSection && sectionContent[currentSection]) {
      sectionContent[currentSection].push(trimmed);
    }
  }

  // Assign parsed sections
  for (const [key, contentLines] of Object.entries(sectionContent)) {
    (note as any)[key] = contentLines.join('\n');
  }

  // If no sections were detected, put everything into procedures
  const hasAnyContent = Object.values(sectionContent).some(v => v.length > 0);
  if (!hasAnyContent) {
    note.procedures = transcript;
  }

  return note;
}

/**
 * Format structured note into a clean, professional operative note.
 * Only includes sections that have content — no empty sections.
 */
export function formatStructuredNote(note: StructuredOpNote): string {
  const sections: string[] = [];

  const addSection = (title: string, content: string) => {
    if (content.trim()) {
      sections.push(`${title}:\n${content.trim()}`);
    }
  };

  addSection('INDICATIONS', note.indications);
  addSection('ANESTHESIA', note.anesthesia);
  addSection('FINDINGS', note.findings);
  addSection('PROCEDURE', note.procedures);
  addSection('IMPLANTS', note.implants);
  addSection('CLOSURE', note.closure);
  addSection('ESTIMATED BLOOD LOSS', note.estimatedBloodLoss);
  addSection('SPECIMENS', note.specimens);
  addSection('COMPLICATIONS', note.complications);

  return sections.join('\n\n');
}
